import React, { useState, useEffect, useCallback } from "react";
import CoachPreferencesSkeleton from "../../components/layout/CoachPreferencesSkeleton";
import api from "../../api/api";
import { useTheme } from "../../hooks/useTheme";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import CoachUpgradeGate from "./CoachUpgradeGate";
import {
    Settings, Plus, Trash2, Save, ClipboardList,
    Sparkles, CheckCircle2, XCircle
} from "lucide-react";

export default function CoachPreferences() {
    const { isDarkMode } = useTheme();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [defaultSuggestions, setDefaultSuggestions] = useState([]);
    const [assignToAll, setAssignToAll] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);
    const [upgradeRequired, setUpgradeRequired] = useState(false);
    const [nudgeEnabled, setNudgeEnabled] = useState(false);
    const [nudgeDay, setNudgeDay] = useState(1);
    const [nudgeHour, setNudgeHour] = useState(9);
    const [mentees, setMentees] = useState([]);
    const [selectedMentees, setSelectedMentees] = useState([]);
    const [bulkText, setBulkText] = useState("");

    // New task form
    const [newTaskTitle, setNewTaskTitle] = useState("");
    const [newTaskDescription, setNewTaskDescription] = useState("");

    useEffect(() => {
        if (!user?.isCoach && !user?.isAdmin) navigate("/userdashboard");
    }, [user, navigate]);

    const fetchPreferences = useCallback(async () => {
        try {
            setLoading(true);
            const [prefRes, nudgeRes] = await Promise.all([
                api.get("/coach/coach-preferences"),
                api.get("/coach/weekly-nudges"),
            ]);
            setDefaultSuggestions(prefRes.data.defaultSuggestions || []);
            setAssignToAll(prefRes.data.assignToAll || false);
            setNudgeEnabled(!!nudgeRes.data.enabled);
            setNudgeDay(Number.isFinite(Number(nudgeRes.data.dayOfWeek)) ? Number(nudgeRes.data.dayOfWeek) : 1);
            setNudgeHour(Number.isFinite(Number(nudgeRes.data.hourOfDay)) ? Number(nudgeRes.data.hourOfDay) : 9);

            const menteeRes = await api.get("/coach/my-mentees");
            setMentees(menteeRes.data.mentees || []);
        } catch (err) {
            if (err.response?.status === 403 && err.response?.data?.code === "COACH_UPGRADE_REQUIRED") {
                setUpgradeRequired(true);
            }
            setDefaultSuggestions([]);
            setAssignToAll(false);
            setNudgeEnabled(false);
            setNudgeDay(1);
            setNudgeHour(9);
            setMentees([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchPreferences(); }, [fetchPreferences]);

    const showMsg = (text, type = "success") => {
        setMessage({ text, type });
        setTimeout(() => setMessage(null), 4000);
    };

    const handleAddTask = () => {
        if (!newTaskTitle.trim()) {
            showMsg("Suggestion title is required", "error");
            return;
        }
        setDefaultSuggestions(prev => [...prev, {
            id: `new_${Date.now()}`,
            title: newTaskTitle.trim(),
            description: newTaskDescription.trim(),
        }]);
        setNewTaskTitle("");
        setNewTaskDescription("");
    };

    const handleRemoveTask = (index) => {
        setDefaultSuggestions(prev => prev.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await api.put("/coach/coach-preferences", {
                defaultSuggestions: defaultSuggestions.map(t => ({
                    title: t.Title || t.title,
                    description: t.Description || t.description || "",
                })),
                assignToAll: assignToAll,
            });
            await api.put("/coach/weekly-nudges", {
                enabled: nudgeEnabled,
                dayOfWeek: nudgeDay,
                hourOfDay: nudgeHour,
            });
                            showMsg("Profile preferences saved successfully");
            fetchPreferences();
        } catch (err) {
            showMsg(err.response?.data?.error || "Failed to save profile preferences", "error");
        } finally {
            setSaving(false);
        }
    };

    const inputClass = `w-full border rounded-xl px-3 py-2.5 text-sm ${isDarkMode ? "bg-slate-700 border-slate-600 text-slate-200 placeholder-slate-400" : "bg-white border-slate-300 text-slate-700 placeholder-slate-400"}`;
    const labelClass = `block text-sm font-medium mb-1 ${isDarkMode ? "text-slate-300" : "text-slate-600"}`;
    const dayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    const runNudgesNow = async () => {
        try {
            setSaving(true);
            const res = await api.post("/coach/weekly-nudges/run");
            showMsg(`Weekly nudges processed (${res.data.created || 0} suggestions created)`);
        } catch (err) {
            showMsg(err.response?.data?.error || "Failed to run weekly nudges", "error");
        } finally {
            setSaving(false);
        }
    };

    const toggleMenteeSelection = (clientId) => {
        setSelectedMentees((prev) => (
            prev.includes(clientId)
                ? prev.filter((id) => id !== clientId)
                : [...prev, clientId]
        ));
    };

    const applyBulkSuggestions = async () => {
        const suggestions = bulkText
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean)
            .map((line) => ({ title: line, description: "" }));

        if (!selectedMentees.length) {
            //old showMsg("Select at least one mentee", "error");
            //old showMsg("Select at least one student", "error");
            showMsg("Select at least one client", "error");
            return;
        }
        if (!suggestions.length) {
            showMsg("Add at least one suggestion line", "error");
            return;
        }

        try {
            setSaving(true);
            const res = await api.post("/coach/suggest-bundle", {
                client_ids: selectedMentees,
                suggestions,
            });
            showMsg(`Bundle applied (${res.data.created || 0} suggestions created)`);
            setBulkText("");
        } catch (err) {
            showMsg(err.response?.data?.error || "Failed to apply bundle", "error");
        } finally {
            setSaving(false);
        }
    };

    const saveBundleAsDefault = async () => {
        const bundleSuggestions = bulkText
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean)
            .map((line) => ({ title: line, description: "" }));

        if (!bundleSuggestions.length) {
            showMsg("Add at least one suggestion line to save as the default bundle", "error");
            return;
        }

        const confirmed = window.confirm(
            "Save this bundle as your Default / Onboarding bundle?\n\n" +
            "This replaces your current suggestion library and turns on auto-suggest, " +
            //old "so these suggestions are automatically sent to every new student who joins your coaching."
            "so these suggestions are automatically sent to every new client who joins your coaching."
        );
        if (!confirmed) return;

        try {
            setSaving(true);
            await api.put("/coach/coach-preferences", {
                defaultSuggestions: bundleSuggestions,
                assignToAll: true,
            });
            setDefaultSuggestions(bundleSuggestions.map((s, i) => ({ id: `new_${Date.now()}_${i}`, title: s.title, description: s.description })));
            setAssignToAll(true);
            //old showMsg("Saved as the default / onboarding bundle. Auto-suggest to new students is now ON.");
            showMsg("Saved as the default / onboarding bundle. Auto-suggest to new clients is now ON.");
        } catch (err) {
            showMsg(err.response?.data?.error || "Failed to save default bundle", "error");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <CoachPreferencesSkeleton isDarkMode={isDarkMode} />;
    }

    if (upgradeRequired) {
        return <CoachUpgradeGate featureName="My Program" />;
    }

    return (
        <div className={`min-h-screen ${isDarkMode ? "bg-surface" : "bg-surface"}`}>
            <div className="max-w-5xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="rounded-2xl overflow-hidden mb-8 shadow-sm">
                    <div className={`relative px-6 py-8 ${isDarkMode ? "bg-gradient-to-r from-purple-900 via-violet-900 to-fuchsia-900" : "bg-gradient-to-r from-purple-600 via-violet-600 to-fuchsia-600"}`}>
                        <Sparkles className="absolute top-4 right-6 text-white/20" size={40} />
                        <div className="relative flex items-center gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-white shadow-lg border border-white/30">
                                <Settings size={28} />
                            </div>
                            <div>
                                <h1 className="text-2xl font-extrabold tracking-tight text-white">My Program</h1>
                                <p className="text-sm text-white/70">Manage your coaching profile preferences, nudges, and suggestion library</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Message toast */}
                {message && (
                    <div role="alert" className={`mb-5 flex items-center gap-3 p-3.5 rounded-xl text-sm font-medium ${
                        message.type === "error"
                            ? isDarkMode ? "bg-red-900/30 text-red-300 border border-red-800/50" : "bg-red-50 text-red-800 border border-red-200"
                            : isDarkMode ? "bg-primary-900/30 text-primary-300 border border-primary-800/50" : "bg-primary-50 text-primary-800 border border-primary-200"
                    }`}>
                        {message.text}
                    </div>
                )}

                {(
                    <>
                        {/* Assign to All Students - Radio Group */}
                        <div className={`rounded-2xl border p-6 mb-6 ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200 shadow-sm"}`}>
                            <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                                {/* old Auto-Suggest to Students */}
                                Auto-Suggest to Clients
                            </h3>
                            <p className={`text-sm mb-4 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                                {/* old Automatically suggest your default suggestions to all new students when they join your coaching. */}
                                Automatically suggest your default suggestions to all new clients when they join your coaching.
                            </p>
                            <div className="flex gap-6">
                                <label className={`flex items-center gap-3 cursor-pointer px-4 py-3 rounded-xl border transition-all ${
                                    assignToAll
                                        ? isDarkMode ? "bg-primary-900/30 border-primary-700 text-primary-300" : "bg-primary-50 border-primary-300 text-primary-700"
                                        : isDarkMode ? "bg-slate-700/50 border-slate-600 text-slate-300" : "bg-slate-50 border-slate-200 text-slate-600"
                                }`}>
                                    <input
                                        type="radio"
                                        name="assignToAll"
                                        checked={assignToAll}
                                        onChange={() => setAssignToAll(true)}
                                        className="accent-primary-600 w-4 h-4"
                                    />
                                    <div className="flex items-center gap-2">
                                        <CheckCircle2 size={18} />
                                        <span className="font-medium text-sm">Yes</span>
                                    </div>
                                </label>
                                <label className={`flex items-center gap-3 cursor-pointer px-4 py-3 rounded-xl border transition-all ${
                                    !assignToAll
                                        ? isDarkMode ? "bg-slate-600/50 border-slate-500 text-slate-200" : "bg-slate-100 border-slate-300 text-slate-700"
                                        : isDarkMode ? "bg-slate-700/50 border-slate-600 text-slate-300" : "bg-slate-50 border-slate-200 text-slate-600"
                                }`}>
                                    <input
                                        type="radio"
                                        name="assignToAll"
                                        checked={!assignToAll}
                                        onChange={() => setAssignToAll(false)}
                                        className="accent-slate-600 w-4 h-4"
                                    />
                                    <div className="flex items-center gap-2">
                                        <XCircle size={18} />
                                        <span className="font-medium text-sm">No</span>
                                    </div>
                                </label>
                            </div>
                        </div>

                        <div className={`rounded-2xl border p-6 mb-6 ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200 shadow-sm"}`}>
                            <div className="flex items-center justify-between gap-3 mb-3">
                                <h3 className={`text-lg font-bold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                                    Bulk Apply Suggestions
                                </h3>
                                <button
                                    type="button"
                                    onClick={() => navigate("/coaching/students")}
                                    className={`px-3 py-2 rounded-lg text-xs font-semibold ${isDarkMode ? "bg-indigo-700 text-white hover:bg-indigo-600" : "bg-indigo-600 text-white hover:bg-indigo-700"}`}
                                >
                                    {/* old Quick Nav: Add Tasks for Students */}
                                    Quick Nav: Add Tasks for Clients
                                </button>
                            </div>
                            <p className={`text-sm mb-3 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                                {/* old Pick mentees and paste one suggestion per line. This pushes all lines as pending suggestions. */}
                                {/* old Pick students and paste one suggestion per line. This pushes all lines as pending suggestions. */}
                                Pick clients and paste one suggestion per line. This pushes all lines as pending suggestions.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    {/* old <label className={labelClass}>Select Mentees</label> */}
                                    {/* old <label className={labelClass}>Select Students</label> */}
                                    <label className={labelClass}>Select Clients</label>
                                    <div className={`rounded-xl border p-3 max-h-44 overflow-auto ${isDarkMode ? "bg-slate-700 border-slate-600" : "bg-slate-50 border-slate-200"}`}>
                                        {mentees.length === 0 ? (
                                            //old <p className={`text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>No mentees found</p>
                                            //old <p className={`text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>No students found</p>
                                            <p className={`text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>No clients found</p>
                                        ) : (
                                            <div className="space-y-1.5">
                                                {mentees.map((m) => (
                                                    <label key={m.ClientID} className="flex items-center gap-2 text-sm">
                                                        <input type="checkbox" checked={selectedMentees.includes(m.ClientID)} onChange={() => toggleMenteeSelection(m.ClientID)} />
                                                        <span className={isDarkMode ? "text-slate-200" : "text-slate-700"}>{m.FirstName} {m.LastName}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <label className={labelClass}>Suggestion Bundle (one per line)</label>
                                    <textarea
                                        rows={6}
                                        value={bulkText}
                                        onChange={(e) => setBulkText(e.target.value)}
                                        className={`${inputClass} resize-none`}
                                        placeholder={"Check in on quarterly goals\nUpdate LinkedIn headline\nSchedule one mock interview"}
                                    />
                                </div>
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    onClick={applyBulkSuggestions}
                                    disabled={saving}
                                    className="px-4 py-2 rounded-xl text-sm font-semibold bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                                >
                                    {/* old Apply Bundle to Selected Mentees */}
                                    {/* old Apply Bundle to Selected Students */}
                                    Apply Bundle to Selected Clients
                                </button>
                                <button
                                    type="button"
                                    onClick={saveBundleAsDefault}
                                    disabled={saving}
                                    //old title="Save this bundle as your default onboarding bundle. It will auto-suggest to every new student."
                                    title="Save this bundle as your default onboarding bundle. It will auto-suggest to every new client."
                                    className={`px-4 py-2 rounded-xl text-sm font-semibold disabled:opacity-50 ${isDarkMode ? "bg-purple-700 text-white hover:bg-purple-600" : "bg-purple-600 text-white hover:bg-purple-700"}`}
                                >
                                    Save as Default / Onboarding Bundle
                                </button>
                            </div>
                        </div>

                        <div className={`rounded-2xl border p-6 mb-6 ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200 shadow-sm"}`}>
                            <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                                Scheduled Weekly Nudges
                            </h3>
                            <p className={`text-sm mb-4 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                                {/* old Automatically send your default suggestions every week to active mentees. */}
                                {/* old Automatically send your default suggestions every week to active students. */}
                                Automatically send your default suggestions every week to active clients.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <div>
                                    <label className={labelClass}>Enabled</label>
                                    <select value={nudgeEnabled ? "1" : "0"} onChange={(e) => setNudgeEnabled(e.target.value === "1")} className={inputClass}>
                                        <option value="1">Yes</option>
                                        <option value="0">No</option>
                                    </select>
                                </div>
                                <div>
                                    <label className={labelClass}>Day</label>
                                    <select value={nudgeDay} onChange={(e) => setNudgeDay(Number(e.target.value))} className={inputClass}>
                                        {dayLabels.map((d, i) => <option key={d} value={i}>{d}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className={labelClass}>Hour (0-23)</label>
                                    <input type="number" min="0" max="23" value={nudgeHour} onChange={(e) => setNudgeHour(Math.max(0, Math.min(23, Number(e.target.value) || 0)))} className={inputClass} />
                                </div>
                            </div>
                            <div className="mt-4">
                                <button
                                    type="button"
                                    onClick={runNudgesNow}
                                    disabled={saving}
                                    className={`px-4 py-2 rounded-xl text-sm font-semibold ${isDarkMode ? "bg-sky-700 text-white hover:bg-sky-600" : "bg-sky-600 text-white hover:bg-sky-700"}`}
                                >
                                    Run Nudges Now
                                </button>
                            </div>
                        </div>

                        {/* Default Task List */}
                        <div className={`rounded-2xl border p-6 mb-6 ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200 shadow-sm"}`}>
                            <div className="flex items-center justify-between mb-4">
                                <h3 className={`text-lg font-bold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                                    Saved Suggestion Library
                                </h3>
                                <span className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                                    {defaultSuggestions.length} suggestion{defaultSuggestions.length !== 1 ? "s" : ""}
                                </span>
                            </div>

                            {/* Existing Tasks */}
                            {defaultSuggestions.length === 0 ? (
                                <div className={`text-center py-8 rounded-xl border-2 border-dashed mb-4 ${isDarkMode ? "border-slate-600" : "border-slate-200"}`}>
                                    <ClipboardList className={`mx-auto mb-2 ${isDarkMode ? "text-slate-500" : "text-slate-300"}`} size={36} />
                                    <p className={`text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>No default suggestions yet. Add your first suggestion below.</p>
                                </div>
                            ) : (
                                <div className="space-y-3 mb-4">
                                    {defaultSuggestions.map((task, index) => (
                                        <div key={task.id || index} className={`flex items-start gap-3 p-3 rounded-xl border ${isDarkMode ? "bg-slate-700/50 border-slate-600" : "bg-slate-50 border-slate-200"}`}>
                                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${isDarkMode ? "bg-slate-600 text-slate-300" : "bg-slate-200 text-slate-500"}`}>
                                                <span className="text-xs font-bold">{index + 1}</span>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className={`font-medium text-sm ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>{task.Title || task.title}</p>
                                                {(task.Description || task.description) && (
                                                    <p className={`text-xs mt-0.5 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{task.Description || task.description}</p>
                                                )}
                                            </div>
                                            <button
                                                onClick={() => handleRemoveTask(index)}
                                                className="p-1.5 rounded-lg text-red-500 hover:bg-red-500/10 transition-colors flex-shrink-0"
                                                title="Remove task"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Add New Task */}
                            <div className={`p-4 rounded-xl border-2 border-dashed ${isDarkMode ? "border-slate-600" : "border-slate-300"}`}>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label className={labelClass}>Suggestion Title *</label>
                                        <input
                                            type="text"
                                            className={inputClass}
                                            placeholder="e.g., Update LinkedIn profile"
                                            value={newTaskTitle}
                                            onChange={e => setNewTaskTitle(e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Description (optional)</label>
                                        <input
                                            type="text"
                                            className={inputClass}
                                            placeholder="Brief description..."
                                            value={newTaskDescription}
                                            onChange={e => setNewTaskDescription(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <button
                                    onClick={handleAddTask}
                                    className={`mt-3 flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${isDarkMode ? "bg-slate-700 text-slate-200 hover:bg-slate-600" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                                >
                                    <Plus size={16} />
                                    Add Suggestion
                                </button>
                            </div>
                        </div>

                        {/* Save Button */}
                        <div className="flex justify-end">
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-primary-600 text-white font-semibold text-sm hover:bg-primary-700 transition-colors shadow-sm disabled:opacity-50"
                            >
                                <Save size={16} />
                                {saving ? "Saving..." : "Save Suggestions"}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
